import React, {useEffect, useState} from 'react'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Paper,
  TextField,
  Autocomplete,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import config from '../config';
import {
  PageContainer,
  ContentContainer, 
  StyledTableCell, 
  Header, 
  StyledTableRow
} from '../components/ContentContainer';

const formatStartTime = (startTime) => {
  const timeCreatedAsInt = parseInt(startTime);
  const timeCreated = new Date(timeCreatedAsInt * 1000);
  const currentTime = new Date()
  const diffInMilliseconds = currentTime - timeCreated;
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  
  if (diffInHours < 3) {
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    return `${Math.floor(diffInMinutes / 60)} hours ${diffInMinutes % 60} minutes ago`;
  }    
  
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const day = `${timeCreated.getDate()}`.slice(-2);
  const month = months[timeCreated.getMonth()];
  const minutes = `${timeCreated.getMinutes()}`.padStart(2, '0');
  const time = `${timeCreated.getHours()}:${minutes}`;
  return `${day} ${month} at ${time}`;
};

const RendersListPage = ({}) => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Unique filter options
  const [statusOptions, setStatusOptions] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSessions().then((sessions) => {
      setSessions(sessions);
      
      // Generate unique filter options
      const statuses = [...new Set(sessions.map(session => session.session_status))];

      setStatusOptions(statuses);
      setFilteredSessions(sessions);
      setIsLoading(false);
    }).catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  // Filter logic
  useEffect(() => {
    const filtered = sessions.filter(session => {
      const sessionDate = new Date(parseInt(session.time_created) * 1000);
      
      const statusMatch = selectedStatuses.length === 0 || 
        selectedStatuses.includes(session.session_status);

      const searchMatch = !searchQuery || 
        (session.product_title && session.product_title.toLowerCase().includes(searchQuery.toLowerCase())) || 
        (session.session_id && String(session.session_id).toLowerCase().includes(searchQuery.toLowerCase()));

      const dateMatch = (!startDate || sessionDate >= startDate) && 
                        (!endDate || sessionDate <= endDate);

      return statusMatch && searchMatch && dateMatch;
    });

    setFilteredSessions(filtered);
  }, [searchQuery, selectedStatuses, startDate, endDate, sessions]);

  const fetchSessions = async () => {
    const response = await fetch(`${config.serverUrl}/sessions`);
    if (!response.ok){
      throw new Error('Failed to fetch sessions');
    }

    const data = await response.json();
    return data.sessions;
  }

  const navigateToReviewPage = ({sessionId}) => {
    navigate(`/review?session_id=${sessionId}`);
  }

  // Autocomplete filter component
  const FilterAutocomplete = ({
    label, 
    options, 
    selectedValues, 
    setSelectedValues
  }) => (
    <Autocomplete
      multiple
      options={options}
      value={selectedValues}
      onChange={(_, newValue) => setSelectedValues(newValue)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip 
            {...getTagProps({ index })} 
            key={option} 
            label={option} 
            size="small"
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText' 
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          variant="outlined" 
          size="small"
        />
      )}
      sx={{ minWidth: 200, mr: 2 }}
    />
  );

  return (
    <PageContainer>
        <Sidebar navigate={navigate} />
        <ContentContainer>
          <Header>Sessions</Header>
          
          {/* Filter Section - Hidden while loading */}
          {!isLoading && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3, 
                  flexWrap: 'wrap' 
                }}
              >
                <TextField
                  label="Search Product"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 200, mr: 2 }}
                />
                <FilterAutocomplete 
                  label="Status" 
                  options={statusOptions} 
                  selectedValues={selectedStatuses}
                  setSelectedValues={setSelectedStatuses}
                />
               <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  sx={{ ml: 2 }}
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', mr: 2} }}
                />
                <FormControl sx={{ minWidth: 120, mr: 2, ml: 2 }}>
                  <InputLabel>Items</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Show items"
                    onChange={(e) => setItemsPerPage(e.target.value)}
                    size="small"
                  >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={filteredSessions.length}>All</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </LocalizationProvider>
          )}

          <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="Rendering Sessions Table">
            <thead>
              <tr>
                <StyledTableCell>Product Title</StyledTableCell>
                <StyledTableCell>Session ID</StyledTableCell>
                <StyledTableCell>Created At</StyledTableCell>
                <StyledTableCell>Wrist Size</StyledTableCell>
                <StyledTableCell>Product File</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Render Time</StyledTableCell>
              </tr>
            </thead>
            <TableBody>
              {filteredSessions.slice(0, itemsPerPage).map((session) => (
                <StyledTableRow 
                  status={session.session_status} 
                  key={session.id} 
                  onClick={() => navigateToReviewPage({sessionId: session.session_id})}
                >
                  <StyledTableCell>{session.product_title}</StyledTableCell>
                  <StyledTableCell>{session.session_id}</StyledTableCell>
                  <StyledTableCell>{formatStartTime(session.time_created)}</StyledTableCell>
                  <StyledTableCell>{session.wrist_size}</StyledTableCell>
                  <StyledTableCell>{session.product_file.split('/').pop()}</StyledTableCell>
                  <StyledTableCell status={session.session_status}>{session.session_status}</StyledTableCell>
                  <StyledTableCell>
                    {session.time_elapsed !== undefined && session.time_elapsed !== null ? (
                      `${Math.floor(session.time_elapsed / 60)} min ${Math.round(session.time_elapsed % 60)} sec`
                    ) : ''}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ContentContainer>
    </PageContainer>
  )
}

export default RendersListPage